<template>
  <div class="txm" ref="root">
    <slot name="trigger" :toggle="toggle" :open="open" />

    <div
      v-if="open"
      class="txm-menu"
      :class="[`txm-align-${align}`, menuClass]"
      role="menu"
      ref="menu"
    >
      <slot :close="close" />
    </div>
  </div>
</template>

<script>
/**
 * Small anchored dropdown for the Builder's top bar.
 *
 * Hand-rolled rather than BootstrapVue's b-dropdown: this tree deliberately
 * builds its own chrome (see the "no BootstrapVue modal in this tree" note on
 * the Builder's remove-confirm), and b-dropdown drags in Bootstrap's visual
 * language, which the Builder otherwise avoids.
 *
 * The close-on-outside-click handling is lifted from BuilderLeftPane's
 * `.bl-multi-menu` — a document-level `mousedown` listener whose bound
 * reference is kept on the instance so beforeDestroy can remove the exact one
 * it added. Two differences from that original:
 *
 *   1. This menu is `position: absolute`. `.bl-multi-menu` is in-flow and
 *      pushes the sidebar's contents down, which is fine in a vertical panel
 *      and would be very wrong in a sticky toolbar.
 *   2. No "Done" button. That one exists because its menu is a multi-select
 *      checkbox list where picking an item doesn't dismiss anything. Both
 *      consumers here close on selection, so a Done button would be noise.
 *
 * The trigger is a scoped slot so each consumer owns its own button markup —
 * a plan switcher and a split-button caret look nothing alike.
 */
export default {
  name: 'TxbMenu',

  props: {
    /** Which edge the menu hangs from. */
    align: { type: String, default: 'left', validator: (v) => ['left', 'right'].includes(v) },
    /** Extra class for the panel, so consumers can size it. */
    menuClass: { type: String, default: '' },
  },

  data() {
    return {
      open: false,
      // Bound listeners kept on the instance so beforeDestroy removes the exact
      // references it added — the leak the original pattern was careful about.
      onDocMouseDown: null,
      onDocKeyDown: null,
    };
  },

  mounted() {
    // Bound once here rather than on each open, so the reference is stable
    // across open/close cycles and teardown is unambiguous. Document-level
    // rather than per-element so it survives the menu's own v-if remount.
    this.onDocMouseDown = (e) => {
      if (!this.open) return;
      const root = this.$refs.root;
      if (root && root.contains(e.target)) return;
      this.open = false;
    };
    this.onDocKeyDown = (e) => {
      if (this.open && e.key === 'Escape') this.open = false;
    };
    document.addEventListener('mousedown', this.onDocMouseDown);
    document.addEventListener('keydown', this.onDocKeyDown);
  },

  beforeDestroy() {
    if (this.onDocMouseDown) {
      document.removeEventListener('mousedown', this.onDocMouseDown);
      this.onDocMouseDown = null;
    }
    if (this.onDocKeyDown) {
      document.removeEventListener('keydown', this.onDocKeyDown);
      this.onDocKeyDown = null;
    }
  },

  methods: {
    toggle() {
      this.open = !this.open;
    },
    close() {
      this.open = false;
    },
  },
};
</script>

<style scoped>
.txm {
  position: relative;
  display: inline-flex;
}

.txm-menu {
  position: absolute;
  top: calc(100% + 6px);
  /* Above the sticky top bar's own stacking context (z-index 20) but well
     below the comments slide-over (100) and the modals (1200). */
  z-index: 30;
  min-width: 200px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
  background: var(--txb-surface, #ffffff);
  border: 1px solid var(--txb-border, #e4e7ec);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06);
}
.txm-align-left { left: 0; }
.txm-align-right { right: 0; }

.txm-menu::-webkit-scrollbar { width: 8px; }
.txm-menu::-webkit-scrollbar-thumb {
  background: var(--txb-border-strong, #cdd3dc);
  border-radius: 4px;
}
</style>
